import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { escapeHtml, unescapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-pro-space-select', {
  placeholder: 'Sélectionnez une option',
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-pro-space-select', {
  placeholder: 'Select an option',
  choose: 'Choose',
})

export interface LvcProSpaceSelect {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export interface Item {
  id: string
  labelFr: string
  labelEn: string
}

export class LvcProSpaceSelect extends MinzeElement {
  private selectedIds: string[] = []
  private filter: string = ''
  private items: Item[] = []
  attrs: Attrs = [
    'title',
    ['chooseLabel', Locale.getKey('lvc-pro-space-select.choose')],
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['background', 'transparent'],
    ['border', ''],
    ['dropdownBorder', ''],
    ['items', []],
    ['error', false],
    ['can-select-multiple', true],
  ]

  reactive: Reactive = [
    ['value', ''],
    ['items', []],
  ]

  static observedAttributes = ['title', 'on-change', 'default-value', 'items', 'error']

  onBlur(e: Event) {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
    if (name === 'items') {
      this.items = JSON.parse(decodeURIComponent(newValue || '[]'))
      this.rerender()
    }
    if (name === 'error') {
      this.error = newValue || ''
      this.rerender()
    }
  }

  updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map(
        (item: any) => `${item.id}`
      )
      this.value = this.defaultValue
    }
    this.rerender()
  }

  onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onItemClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    // if the user clicked "add an other value"
    if (value === 'other') {
      const otherInput = this.select('[name="other"]') as HTMLInputElement

      if (!otherInput) {
        return
      }

      const otherValue = unescapeHtml(otherInput.value.trim())

      if (!otherValue) {
        return
      }

      if (otherValue === '') {
        this.rerender()
        return
      }

      // Generate an ID from the entered value, we remove accents, special characters and make the id camel case
      // with "custom-" before the id to make sure it's unique
      const id = `custom-${otherValue
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '-')
        .toLowerCase()}`

      // if the item already exists, we don't add it
      if (
        this.items.find(
          item =>
            item.id === id &&
            (item.labelFr.toLowerCase() === otherValue.toLowerCase() ||
              item.labelEn.toLowerCase() === otherValue.toLowerCase())
        )
      ) {
        return
      }

      // push the new item to the list of all the options
      let otherValueLabel = otherValue.charAt(0).toUpperCase() + otherValue.slice(1)
      this.items.splice(this.items.length - 1, 0, {
        id,
        labelFr: otherValueLabel,
        labelEn: otherValueLabel,
      })

      // Select the new item
      this.selectedIds.push(id)
      this.serializeValue()
      this.rerender()
      return
    }

    // if the user can only select one item, we remove the other selected items
    if (!this.canSelectMultiple) {
      this.selectedIds = [value]
      // else we add the item to the selected items
    } else {
      // Clicked on a specific item, it selects it
      let index = this.selectedIds.indexOf(value)

      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(value)
      }

      // Check if "allItems" is selected and deselect it
      let allItemsIndex = this.selectedIds.indexOf('allItems')
      if (allItemsIndex > -1) {
        this.selectedIds.splice(allItemsIndex, 1)
      }
    }

    this.serializeValue()
    this.rerender()
  }

  onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let objects = this.items.filter(item => this.selectedIds.indexOf(item.id) > -1)
    objects = objects.filter(item => item.id !== 'other')
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }

  getItemLabel() {
    const language = Locale.language
    if (this.selectedIds.length === 0 && !this.chooseLabel) {
      return Locale.getKey('lvc-pro-space-select.choose')
    } else if (this.selectedIds.length === 0 && this.chooseLabel) {
      return this.chooseLabel
    }

    // it gets the labels of the selected items but not the "other" item
    let labels = this.items
      .filter(item => this.selectedIds.indexOf(`${item.id}`) > -1)
      .filter(item => item.id !== 'other')
      .map(item => (language === 'fr' ? item.labelFr : item.labelEn))

      // if the text is too long, we cut it and add "..."
      .map(label => (label.length > 50 ? label.substring(0, 50) + ' (...)' : label))

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderItem = (item: any) => {
    if (this.filter && item.name.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedIds.indexOf(`${item.id}`) == -1

    if (item.id === 'other') {
      return `
        <lvc-box display="flex" alignItems="center">
          <lvc-input name="other" padding="8px 8px" placeholder="${Locale.getKey('lvc-ticketing-services-field.other')}" value="${escapeHtml(item.name)}" on-change="serializeValue" background="transparent"></lvc-input>
          <lvc-button enable-hover-box-shadow="true" name="ticketingService" padding="10px 20px 10px 20px" height="fit-content" data-id="${item.id}">${Locale.getKey('lvc-ticketing-services-field.add')}</lvc-button>
        </lvc-box>
      `
    }

    return `
      <button 
        name="item"
        class="${isNotSelected ? 'not-selected' : 'selected'} itemButton" 
        data-id="${item.id}">
        ${Locale.language === 'fr' ? item.labelFr : item.labelEn}
        ${isNotSelected ? '' : `<lvc-circle-check-select-icon data-id="${item.id}"></lvc-circle-check-select-icon>`}
      </button>
    `
  }

  renderItems = () => {
    let html = ''

    for (let i = 0; i < this.items.length; i++) {
      html += this.renderItem(this.items[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0

    return /*html*/ `
      <div class="title">${this.title || Locale.getKey('lvc-pro-space-select.title')}</div>
      <div class="input">
        <button class="dropbtn" name="toggle">
          ${this.getItemLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-pro-space-select.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="items" data-selected="${this.selectedIds.length}">
            ${this.renderItems()}
          </div>
        </div> 
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return /*css*/ `

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        font-family: inherit;
        color: #1E007D;
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        background: ${this.background ? this.background : 'transparent'};
        position: relative;
        border: ${this.border ? this.border : '1px solid var(--Minuit, #1E007D);'}; 
        border-radius: 24px;
      }

      .items {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .items button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: auto;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .items button:last-child {
        border-bottom: none;
      }

      .items button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 16px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        overflow: hidden; 
        text-overflow: ellipsis; 
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: 100%;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: ${this.dropdownBorder ? this.dropdownBorder : '1px solid var(--Minuit, #1E007D);'}; 
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="item"]', 'click', this.onItemClick.bind(this)],
    ['lvc-button[name="ticketingService"]', 'click', this.onItemClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
